/**
 * 地址转经纬度工具（免费方案）
 *
 * 1. Nominatim (OpenStreetMap) - 完全免费，无需 Key，限制 1 请求/秒
 * 2. 高德地图 - 项目已配置 Key，有免费额度，国内地址精度更好
 */

import BasicConfig from '@/config/basic'

const NOMINATIM_DELAY_MS = 1100 // Nominatim 要求 1 请求/秒

/**
 * 延迟函数
 * @param {number} ms - 毫秒
 */
const delay = (ms) => new Promise((r) => setTimeout(r, ms))

/**
 * 使用 Nominatim 免费地理编码（地址 → 经纬度）
 * 完全免费，无需 API Key，限制 1 请求/秒
 * @param {string} address - 地址，如 "广州市天河区体育西路"
 * @param {string} [countryCode='cn'] - 国家代码，限定范围提高精度
 * @returns {Promise<[number, number]|null>} [经度, 纬度] 或 null
 */
export async function addressToCoordNominatim(address, countryCode = 'cn') {
  if (!address || typeof address !== 'string' || !address.trim()) return null

  const params = new URLSearchParams({
    q: address.trim(),
    format: 'json',
    limit: 1,
    countrycodes: countryCode
  })

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?${params}`,
      {
        headers: {
          'User-Agent': 'AegisMaternity/1.0 (contact@example.com)'
        }
      }
    )
    const data = await res.json()
    if (Array.isArray(data) && data.length > 0) {
      const { lat, lon } = data[0]
      return [parseFloat(lon), parseFloat(lat)]
    }
  } catch (e) {
    console.warn('Nominatim 地理编码失败:', e)
  }
  return null
}

/**
 * 使用高德地图地理编码（地址 → 经纬度）
 * 项目已配置 Key，有免费额度，国内地址精度更好
 * @param {string} address - 地址
 * @param {string} [city] - 可选，限定城市提高精度
 * @returns {Promise<[number, number]|null>} [经度, 纬度] 或 null
 */
export async function addressToCoordAmap(address, city) {
  const key = BasicConfig.amapKey || ''
  if (!key || !address?.trim()) return null

  const params = new URLSearchParams({
    key,
    address: address.trim(),
    output: 'json'
  })
  if (city) params.set('city', city)

  try {
    const res = await fetch(`https://restapi.amap.com/v3/geocode/geo?${params}`)
    const data = await res.json()
    if (data.status === '1' && data.geocodes?.length > 0) {
      const [lng, lat] = data.geocodes[0].location.split(',').map(Number)
      return [lng, lat]
    }
  } catch (e) {
    console.warn('高德地理编码失败:', e)
  }
  return null
}

/**
 * 地址转经纬度（优先高德，失败时回退 Nominatim）
 * @param {string} address - 地址
 * @param {Object} [opts] - 选项
 * @param {string} [opts.city] - 城市（高德用）
 * @param {boolean} [opts.useNominatimOnly] - 仅用 Nominatim（完全免费）
 * @returns {Promise<[number, number]|null>} [经度, 纬度] 或 null
 */
export async function addressToCoord(address, opts = {}) {
  const { city, useNominatimOnly = false } = opts

  if (useNominatimOnly) {
    return addressToCoordNominatim(address)
  }

  const coord = await addressToCoordAmap(address, city)
  if (coord) return coord

  return addressToCoordNominatim(address)
}
